import { useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { Provider } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/provider.pb';
import { DataSourcesQueries } from '@frontend/api-data-sources';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { getFullName } from '@frontend/name';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import {
  Button,
  ButtonBar,
  FormFieldActionTypes,
  FormRow,
  SpinningLoader,
  styles,
  Text,
  TextareaField,
  TextField,
  useAlert,
  useForm,
} from '@frontend/design-system';
import { useGetCustomContactSourceId } from '../../hooks/scheduler-v3/use-get-custom-contact-source-id';
import { useSchedulingLocationInfo } from '../../hooks/use-scheduling-location-info';
import { ProviderEditableAvatar } from './provider-editable-avatar';

interface Props {
  locationId: string;
  providerId?: string;
  providerDetails?: Provider;
  isLoading?: boolean;
  primaryButtonLabel?: string;
  onClose?: () => void;
}

export const ProviderDetailsForm = ({
  locationId,
  providerId,
  isLoading,
  providerDetails,
  primaryButtonLabel,
  onClose,
}: Props) => {
  const alert = useAlert();
  const { t } = useTranslation('schedule');
  const isSmall = useMatchMedia({ maxWidth: breakpoints.small.max });
  const { parentLocationId } = useSchedulingLocationInfo();
  const { customContactSourceId } = useGetCustomContactSourceId();

  const { isIntegratedOffice } = DataSourcesQueries.useGetAccessibleIntegrations();

  const isCreate = !providerId;
  const requireFullName = isIntegratedOffice ? isCreate : true;

  const isDisplayNameChangedManually = useRef(Boolean(providerDetails?.displayName));
  const [providerPhoto, setProviderPhoto] = useState(providerDetails?.profile?.picUrl || '');
  const { formProps, getFieldProps, values, seedValues, isComplete } = useForm({
    fields: {
      firstName: { type: 'text', required: requireFullName, value: providerDetails?.firstName },
      lastName: { type: 'text', required: requireFullName, value: providerDetails?.lastName },
      displayName: { type: 'text', required: true, value: providerDetails?.displayName },
      bio: { type: 'text', required: false, value: providerDetails?.profile?.bio },
      providerWorkingHours: { type: 'dropdown', required: false, value: '' },
    },
    fieldStateReducer: (state, action) => {
      if (
        action.type === FormFieldActionTypes.Update &&
        ['firstName', 'lastName'].includes(action.payload.name) &&
        !isDisplayNameChangedManually.current
      ) {
        // Update displayName when firstName or lastName changes
        const displayName = getFullName({ firstName: state.firstName.value, lastName: state.lastName.value });
        return {
          ...state,
          displayName: {
            ...state.displayName,
            value: displayName,
            error: displayName ? '' : t('Display Name is required'),
          },
        };
      }
      if (action.type === FormFieldActionTypes.Update && action.payload.name === 'displayName') {
        // Set flag to true when displayName is changed manually
        isDisplayNameChangedManually.current = true;
      }
      return state;
    },
  });

  const updateProviderMutation = SchedulerV3.Mutations.useUpdateProviderMutation();
  const createProviderMutation = SchedulerV3.Mutations.useCreateProviderMutation();

  const { isSingleTypeScope } = useAppScopeStore();
  const isMultiLocation = !isSingleTypeScope;

  const { hasWSM, isLoading: isLoadingIntegrationDetails } = SchedulerV3.Helpers.useCheckHasWSM(locationId);

  // Seed values on practitioner details change
  useEffect(() => {
    seedValues({
      firstName: providerDetails?.firstName || '',
      lastName: providerDetails?.lastName || '',
      displayName: providerDetails?.displayName || '',
      bio: providerDetails?.profile?.bio ?? '',
    });
    setProviderPhoto(providerDetails?.profile?.picUrl ?? '');
    isDisplayNameChangedManually.current = Boolean(providerDetails?.displayName);
  }, [providerDetails, seedValues]);

  const getPayloadForUpdateProvider = (childLocationId?: string): SchedulerV3.Types.UpdateProviderIO['input'] => ({
    id: providerId || '',
    firstName: values.firstName || '',
    lastName: values.lastName || '',
    displayName: values.displayName || '',
    hidden: providerDetails?.hidden,
    profile: {
      ...providerDetails?.profile,
      bio: values?.bio,
      picUrl: providerPhoto,
    },
    // Explicitly use the childLocationId if provided, otherwise keep the usage of providerDetails.locationId. Although the providerDetails.locationId is the parentLocationId for a multi-location.
    childLocationId: isMultiLocation ? childLocationId || providerDetails?.locationId : undefined,
  });

  const getPayloadForCreateProvider = (): SchedulerV3.Types.CreateProviderIO['input'] => ({
    locationId: locationId,
    sourceTenantId: locationId || '',
    firstName: values.firstName || '',
    lastName: values.lastName || '',
    displayName: values.displayName || '',
    hidden: false,
    profile: {
      act: true,
      bio: values?.bio,
      picUrl: providerPhoto,
    },
    // for non-integrated location, we need to set the sourceId as customContactSourceId,
    // for integrated location, BE set the sourceId as the integration's SourceID automatically and on FE we were not allowing create provider for integrated location
    sourceId: customContactSourceId,
    parentLocationId: isMultiLocation ? parentLocationId : undefined,
  });

  const handleUpdateProvider = async () => {
    if (!providerId) {
      return;
    }

    const payload = getPayloadForUpdateProvider(locationId);

    return updateProviderMutation
      .mutateAsync(payload)
      .then(() => {
        alert.success(t('Provider details updated successfully'));
        onClose?.();
      })
      .catch((error) => {
        console.error('Error updating provider:', error);
        alert.error(t('Failed to update Provider'));
      });
  };

  const handleCreateProvider = async () => {
    const payload = getPayloadForCreateProvider();

    try {
      await createProviderMutation.mutateAsync(payload);
      alert.success(t('Provider created successfully'));
      onClose?.();
    } catch (error) {
      console.error('Error creating provider:', error);
      alert.error(t('Failed to create Provider'));
    }
  };

  const handleSave = () => {
    providerId ? handleUpdateProvider() : handleCreateProvider();
  };

  if (isLoading || isLoadingIntegrationDetails) {
    return (
      <div css={styles.flexCenter}>
        <SpinningLoader size='small' />
      </div>
    );
  }

  const isSaving = createProviderMutation.isLoading || updateProviderMutation.isLoading;

  return (
    <>
      <form {...formProps} css={{ overflowY: 'auto', overflowX: 'hidden' }}>
        <ProviderEditableAvatar
          locationId={locationId}
          providerName={getFullName(values) || getFullName(providerDetails ?? {})}
          providerPhoto={providerPhoto}
          onChangeProviderPhoto={setProviderPhoto}
        />

        <FormRow cols={[100, 100]} css={practitionerTextFieldContainerStyles(isSmall)}>
          <TextField
            {...getFieldProps('firstName')}
            label={t('First Name')}
            disabled={!hasWSM}
            helperText={!hasWSM ? t("The provider's first name as it appears in your Practice Management System.") : ''}
          />
          <TextField
            {...getFieldProps('lastName')}
            label={t('Last Name')}
            disabled={!hasWSM}
            helperText={!hasWSM ? t("The provider's last name as it appears in your Practice Management System.") : ''}
          />
        </FormRow>
        <FormRow cols={[100]} css={practitionerTextFieldContainerStyles(isSmall)}>
          <TextField
            {...getFieldProps('displayName')}
            label={t('Display Name')}
            helperText={t('This name will be visible to customers when booking online, and on your Weave calendar')}
          />
        </FormRow>
        <FormRow cols={[100]} css={textAreaFieldContainerStyles}>
          <TextareaField label={t('Provider Biography')} {...getFieldProps('bio')} />
        </FormRow>
        <Text size='small' color='subdued'>
          {t(
            "Enter a brief description of the provider's background & expertise. Patients will see this when booking appointments online."
          )}
        </Text>
      </form>
      <ButtonBar css={{ padding: theme.spacing(1, 0, 0, 0) }}>
        <Button disabled={!isComplete} loading={isSaving} onClick={handleSave} size='large' type='button'>
          {primaryButtonLabel || t('Save')}
        </Button>
      </ButtonBar>
    </>
  );
};

const practitionerTextFieldContainerStyles = (isSmall: boolean) =>
  css({
    display: 'flex',
    flexDirection: isSmall ? 'column' : 'row',
    rowGap: theme.spacing(3),
    ...(isSmall ? { marginRight: 0 } : {}),
  });

const textAreaFieldContainerStyles = css({
  marginBottom: theme.spacing(1),
  paddingTop: theme.spacing(1),
  flexDirection: 'column',
});
